// Whiteboard shared geometry + read-only SVG rendering (Phase 3.6A).
// Virtual canvas: all coordinates live in a fixed W x H space and scale responsively via viewBox.
export const WB_W = 1000;
export const WB_H = 600;

export const uid = () => `wb${Date.now().toString(36)}${Math.floor(Math.random() * 1e6).toString(36)}`;

export const textLines = (o) => String(o.text ?? '').split('\n');

export function bbox(o) {
  if (o.type === 'line' || o.type === 'arrow') {
    return { x: Math.min(o.x1, o.x2), y: Math.min(o.y1, o.y2), w: Math.abs(o.x2 - o.x1), h: Math.abs(o.y2 - o.y1) };
  }
  if (o.type === 'path') {
    const xs = o.points.map((p) => p[0]);
    const ys = o.points.map((p) => p[1]);
    const x = Math.min(...xs), y = Math.min(...ys);
    return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
  }
  if (o.type === 'text') {
    const lines = textLines(o);
    const fs = o.fontSize || 24;
    return { x: o.x, y: o.y, w: Math.max(...lines.map((l) => l.length), 1) * fs * 0.56, h: lines.length * fs * 1.25 };
  }
  return { x: o.x, y: o.y, w: o.w, h: o.h };
}

export function moveObj(o, dx, dy) {
  if (o.type === 'line' || o.type === 'arrow') return { ...o, x1: o.x1 + dx, y1: o.y1 + dy, x2: o.x2 + dx, y2: o.y2 + dy };
  if (o.type === 'path') return { ...o, points: o.points.map(([x, y]) => [x + dx, y + dy]) };
  return { ...o, x: o.x + dx, y: o.y + dy };
}

export function resizeObj(o, from, to) {
  if (o.type === 'path') {
    const sx = from.w ? to.w / from.w : 1, sy = from.h ? to.h / from.h : 1;
    return { ...o, points: o.points.map(([x, y]) => [to.x + (x - from.x) * sx, to.y + (y - from.y) * sy]) };
  }
  if (o.type === 'text') {
    const fs = Math.max(8, Math.min(200, Math.round((o.fontSize || 24) * (from.h ? to.h / from.h : 1))));
    return { ...o, x: to.x, y: to.y, fontSize: fs };
  }
  return { ...o, x: to.x, y: to.y, w: to.w, h: to.h };
}

const r1 = (n) => Math.round(n * 10) / 10;

// Image layers may only point at this app's own media store (or an inline image) — never arbitrary hosts.
const MEDIA_PREFIXES = [`${process.env.REACT_APP_BACKEND_URL}/api/media/`, '/api/media/'];
export const safeImageSrc = (src) => (typeof src === 'string'
  && (MEDIA_PREFIXES.some((p) => src.startsWith(p)) || /^data:image\/(png|jpe?g|webp|gif);base64,/.test(src)) ? src : null);

function ImageShape({ o, hit }) {
  const src = safeImageSrc(o.src);
  const box = { x: r1(o.x), y: r1(o.y), width: Math.max(1, r1(o.w)), height: Math.max(1, r1(o.h)) };
  if (!src) return <rect {...box} fill="#F1F5F9" stroke="#CBD5E1" strokeDasharray="6 4" pointerEvents={hit ? 'all' : 'none'} />;
  return <image href={src} {...box} preserveAspectRatio="none" opacity={o.opacity ?? 1} pointerEvents={hit ? 'all' : 'none'} />;
}

const esc = (t) => String(t).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Text is written as raw SVG markup so no JSX text node is ever wrapped in HTML.
const textMarkup = (o, fs) => textLines(o)
  .map((line, i) => `<tspan x="${o.x}" dy="${i === 0 ? 0 : fs * 1.25}">${esc(line || ' ')}</tspan>`).join('');

function arrowHead(o) {
  const a = Math.atan2(o.y2 - o.y1, o.x2 - o.x1);
  const size = 8 + (o.strokeWidth ?? 2) * 3;
  const bx = o.x2 - size * Math.cos(a), by = o.y2 - size * Math.sin(a);
  const px = (size / 2) * Math.sin(a), py = (size / 2) * Math.cos(a);
  return `${r1(o.x2)},${r1(o.y2)} ${r1(bx + px)},${r1(by - py)} ${r1(bx - px)},${r1(by + py)}`;
}

function ArrowHead({ o }) {
  if (o.type !== 'arrow') return null;
  return <polygon points={arrowHead(o)} fill={o.stroke || '#0F172A'} pointerEvents="none" />;
}

function HitLine({ o, hit }) {
  if (!hit) return null;
  return <line x1={o.x1} y1={o.y1} x2={o.x2} y2={o.y2} stroke="transparent" strokeWidth={18} pointerEvents="stroke" />;
}

function HitPolyline({ pts, hit }) {
  if (!hit) return null;
  return <polyline points={pts} fill="none" stroke="transparent" strokeWidth={18} pointerEvents="stroke" />;
}

// NOTE: no inline `{cond && <el/>}` inside SVG — conditional children are rendered by components
// that return null, so tooling that wraps JSX expressions can never inject HTML into the SVG.
function Shape({ o, hit }) {
  const common = { opacity: o.opacity ?? 1, stroke: o.stroke || 'none', strokeWidth: o.strokeWidth ?? 2 };
  const fill = o.fill || 'none';
  switch (o.type) {
    case 'rect':
      return <rect x={r1(o.x)} y={r1(o.y)} width={Math.max(1, r1(o.w))} height={Math.max(1, r1(o.h))} rx={o.radius || 0} fill={fill} {...common} pointerEvents={hit ? 'all' : 'none'} />;
    case 'ellipse':
      return <ellipse cx={r1(o.x + o.w / 2)} cy={r1(o.y + o.h / 2)} rx={Math.max(1, r1(o.w / 2))} ry={Math.max(1, r1(o.h / 2))} fill={fill} {...common} pointerEvents={hit ? 'all' : 'none'} />;
    case 'line':
    case 'arrow':
      return (
        <g opacity={common.opacity}>
          <line x1={o.x1} y1={o.y1} x2={o.x2} y2={o.y2} stroke={o.stroke || '#0F172A'} strokeWidth={common.strokeWidth}
            strokeLinecap="round" pointerEvents="none" />
          <ArrowHead o={o} />
          <HitLine o={o} hit={hit} />
        </g>
      );
    case 'path': {
      const pts = o.points.map((p) => p.map(r1).join(',')).join(' ');
      return o.closed
        ? <polygon points={pts} fill={fill} {...common} strokeLinejoin="round" pointerEvents={hit ? 'all' : 'none'} />
        : (
          <g opacity={common.opacity}>
            <polyline points={pts} fill="none" stroke={o.stroke || '#0F172A'} strokeWidth={common.strokeWidth} strokeLinecap="round" strokeLinejoin="round" pointerEvents="none" />
            <HitPolyline pts={pts} hit={hit} />
          </g>
        );
    }
    case 'image':
      return <ImageShape o={o} hit={hit} />;
    case 'text': {
      const fs = o.fontSize || 24;
      const halo = o.halo ? { stroke: o.halo, strokeWidth: Math.max(3, fs * 0.22), strokeLinejoin: 'round', paintOrder: 'stroke' } : {};
      return (
        <text x={o.x} y={o.y} fontSize={fs} fill={o.color || '#0F172A'} opacity={common.opacity} fontWeight={o.bold ? 700 : 500} {...halo}
          dominantBaseline="hanging" style={{ fontFamily: 'inherit', userSelect: 'none' }} pointerEvents={hit ? 'all' : 'none'}
          dangerouslySetInnerHTML={{ __html: textMarkup(o, fs) }} />
      );
    }
    default:
      return null;
  }
}

const ROTATABLE = new Set(['rect', 'ellipse', 'text', 'image']);

export function rotationTransform(o) {
  if (!ROTATABLE.has(o.type) || !o.rotation) return undefined;
  const b = bbox(o);
  return `rotate(${r1(o.rotation)} ${r1(b.x + b.w / 2)} ${r1(b.y + b.h / 2)})`;
}

export function WhiteboardShapes({ objects, onObjectPointerDown, onObjectDoubleClick }) {
  return objects.filter((o) => !o.hidden).map((o) => (
    <g key={o.id} data-testid={`wb-obj-${o.id}`} data-type={o.type} data-group={o.groupId || undefined} data-locked={o.locked ? 'true' : undefined}
      onPointerDown={onObjectPointerDown ? (e) => onObjectPointerDown(e, o) : undefined}
      onDoubleClick={onObjectDoubleClick ? (e) => onObjectDoubleClick(e, o) : undefined}
      transform={rotationTransform(o)} style={onObjectPointerDown ? { cursor: o.locked ? 'not-allowed' : 'move' } : undefined}>
      <Shape o={o} hit={!!onObjectPointerDown} />
    </g>
  ));
}

// Static, read-only board (shared by the inline view and the full-screen viewer — one renderer).
export function WhiteboardCanvas({ content, className = 'w-full h-auto block' }) {
  const objects = Array.isArray(content.objects) ? content.objects : [];
  return (
    <svg viewBox={`0 0 ${WB_W} ${WB_H}`} className={className} role="img" aria-label={content.title || 'Whiteboard'}>
      <WhiteboardShapes objects={objects} />
    </svg>
  );
}
